import React, { useState, useCallback, useRef, useEffect } from "react";
import { FaShare, FaPlay, FaLink } from "react-icons/fa";
import {
  personaIcon,
  presentationIcon,
  renderCardIcon,
  renderIcon,
  topicIcon,
} from "../../common/IconLibrary";
import ContextMenu from "../../common/ContextMenu";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../common/ToastContext";

const PresentationCard = ({
  item,
  onClick,
  hideContextMenu = false,
  height = null,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [isContextMenuInteraction, setIsContextMenuInteraction] =
    useState(false);
  const parentRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (item?.configuration) {
      const uniqueTopics = new Set();
      const uniquePersonas = new Set();

      item.configuration.forEach((cfg) => {
        uniqueTopics.add(cfg.topic.key || cfg.topic.id);
        uniquePersonas.add(cfg.persona.id);
      });

      setCounts({
        configurations: item?.configuration?.length,
        topics: uniqueTopics.size,
        personas: uniquePersonas.size,
      });
    } else {
      setCounts({ configurations: 0, topics: 0, personas: 0 });
    }
  }, [item]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setContextMenu({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      setIsContextMenuInteraction(true);
    }
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setIsContextMenuInteraction(false);
  }, []);

  const handleCardClick = (event) => {
    if (!isContextMenuInteraction) {
      onClick(event);
    }
  };

  const contextMenuItems = [
    {
      label: "Share",
      icon: FaShare,
      onClick: () => {
        const url = `${window.location.origin}/presentation/${
          item.url_name || item.id
        }`;
        navigator.clipboard.writeText(url);
        closeContextMenu();
        showToast("Link copied to clipboard", "success");
      },
    },
    {
      label: "Start",
      icon: FaPlay,
      onClick: () => {
        navigate(`/presentation/${item.url_name || item.id}`);
        closeContextMenu();
      },
    },
  ];

  const handleGoToPresentation = (e) => {
    e.stopPropagation();
    navigate(`/presentation/${item.url_name || item.id}`);
  };

  return (
    <div
      className={`card-glass ${height ? "" : "h-full"}`}
      style={height ? { height } : {}}
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      ref={parentRef}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center mb-2">
          <span className="text-gray-300 mr-2">
            {renderCardIcon(presentationIcon, 24)}
          </span>
          <div className="flex items-center h-[54px]">
            <h3 className="text-xl font-semibold text-gray-100 text-pretty">
              {item.name}
            </h3>
          </div>
        </div>
        <div className="relative group flex-grow">
          <p className="text-gray-300 mb-4 line-clamp-3 overflow-hidden">
            {item.description}
          </p>
          <div className="hint-glass">{item.description}</div>
        </div>
        <div className="mt-auto">
          <div className="grid grid-cols-[16px_1fr] gap-2 items-center text-gray-300 mb-2">
            <span className="text-gray-400">
              {renderIcon(topicIcon, 18, 18)}
            </span>
            <span className="text-sm">
              {counts.topics} topic
              {counts.topics !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="grid grid-cols-[16px_1fr] gap-2 items-center text-gray-300 mb-2">
            <span className="text-gray-400">
              {renderIcon(personaIcon, 18, 18)}
            </span>
            <span className="text-sm">
              {counts.personas} persona
              {counts.personas !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="grid grid-cols-[16px_1fr] gap-2 items-center text-gray-300 mb-4">
            <FaLink className="text-gray-400" />
            <div
              className="relative text-sm p-2 bg-gray-700/80 hover:bg-gray-700 hover:text-blue-200 rounded overflow-hidden"
              onClick={handleGoToPresentation}
            >
              <span
                className="block truncate"
                style={{ direction: "rtl", textAlign: "left" }}
              >
                {`${window.location.origin}/presentation/${
                  item.url_name || item.id
                }`}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            {item.tags?.map(
              (tag) =>
                tag && (
                  <span
                    key={tag}
                    className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full mr-2 mb-2"
                  >
                    {tag}
                  </span>
                )
            )}
          </div>
        </div>
        {!hideContextMenu && contextMenu && (
          <ContextMenu
            items={contextMenuItems}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={closeContextMenu}
            parentRef={parentRef}
          />
        )}
      </div>
    </div>
  );
};

export default PresentationCard;
